/**
 * The player's saved run recordings, in IndexedDB.
 *
 * Entries:
 *  - pb: the recording of the personal best, saved automatically. One per
 *    track version and movement rules (record key); a new PB replaces it.
 *  - saved: recordings the player kept by hand (named, optional favourite).
 *  - imported: recordings loaded from a file. View-only: they are never
 *    records, never PB ghosts, and never change progress.
 *
 * Every recording read back is parsed with the same untrusted-file parser as
 * an import (storage can be edited or corrupted). The library is bounded
 * (MAX_ENTRIES, MAX_TOTAL_BYTES). When full, saving by hand or importing is
 * refused with a clear message instead of deleting anything the player chose
 * to keep; an automatic PB save makes room by dropping the oldest PB
 * recordings of *other* track versions that are not favourites.
 */
import { StorageError } from '../editor/ProjectStore';
import { parseRecording, serializeRecording, utf8Bytes, MAX_RECORDING_BYTES, type RunRecording } from './RecordingFormat';

export const REPLAY_DB = 'xernous-replays';
const DB_VERSION = 1;
export const MAX_ENTRIES = 200;
export const MAX_TOTAL_BYTES = 64 * 1024 * 1024;
export const MAX_NAME = 80;

export type ReplayKind = 'pb' | 'saved' | 'imported';

export interface ReplayMeta {
  id: string;
  kind: ReplayKind;
  name: string;
  /** Catalog key of the track it was recorded on ('' when unknown, e.g. an import for a track not installed). */
  trackKey: string;
  trackTitle: string;
  recordKey: string;
  createdAt: number;
  savedAt: number;
  seconds: number | null;
  outcome: RunRecording['result']['outcome'];
  favourite: boolean;
  bytes: number;
}

interface ReplayRow extends ReplayMeta {
  text: string;
}

const req = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'));
  });

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });

function wrap(err: unknown, what: string): StorageError {
  if (err instanceof StorageError) return err;
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'QuotaExceededError') return new StorageError('quota', `${what}: the browser's storage for this site is full. Delete old recordings or export them to files.`);
  return new StorageError('failed', `${what}: ${err instanceof Error ? err.message : String(err)}`);
}

/** Display name: trimmed, control characters removed, bounded. */
export function cleanName(name: string): string {
  const s = name.replace(/\p{Cc}/gu, ' ').replace(/\s+/g, ' ').trim();
  return s.slice(0, MAX_NAME);
}

const meta = (r: ReplayRow): ReplayMeta => ({
  id: r.id,
  kind: r.kind,
  name: r.name,
  trackKey: r.trackKey,
  trackTitle: r.trackTitle,
  recordKey: r.recordKey,
  createdAt: r.createdAt,
  savedAt: r.savedAt,
  seconds: r.seconds,
  outcome: r.outcome,
  favourite: r.favourite,
  bytes: r.bytes,
});

/** Rows are shaped by this module, but storage is not trusted: drop anything malformed from listings. */
function isRow(x: unknown): x is ReplayRow {
  if (typeof x !== 'object' || x === null) return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r['id'] === 'string' &&
    (r['kind'] === 'pb' || r['kind'] === 'saved' || r['kind'] === 'imported') &&
    typeof r['name'] === 'string' &&
    typeof r['trackKey'] === 'string' &&
    typeof r['trackTitle'] === 'string' &&
    typeof r['recordKey'] === 'string' &&
    typeof r['createdAt'] === 'number' &&
    typeof r['savedAt'] === 'number' &&
    (r['seconds'] === null || typeof r['seconds'] === 'number') &&
    (r['outcome'] === 'finished' || r['outcome'] === 'incomplete') &&
    typeof r['favourite'] === 'boolean' &&
    typeof r['bytes'] === 'number' &&
    typeof r['text'] === 'string'
  );
}

let counter = 0;
const newId = (): string => `rec-${Date.now().toString(36)}-${(counter++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export class ReplayLibrary {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(factory: IDBFactory | null = globalThis.indexedDB ?? null, name = REPLAY_DB): Promise<ReplayLibrary> {
    if (!factory) throw new StorageError('unavailable', 'This browser has no IndexedDB (private mode may disable it), so recordings cannot be kept. They can still be exported to files.');
    try {
      const r = factory.open(name, DB_VERSION);
      r.onupgradeneeded = () => {
        if (!r.result.objectStoreNames.contains('replays')) r.result.createObjectStore('replays', { keyPath: 'id' });
      };
      return new ReplayLibrary(await req(r));
    } catch (err: unknown) {
      throw new StorageError('unavailable', `Could not open the recording library: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  close(): void {
    this.db.close();
  }

  async list(): Promise<ReplayMeta[]> {
    try {
      const rows = (await req(this.db.transaction('replays').objectStore('replays').getAll())) as unknown[];
      return rows.filter(isRow).map(meta).sort((a, b) => b.savedAt - a.savedAt);
    } catch (err: unknown) {
      throw wrap(err, 'Listing recordings failed');
    }
  }

  /** Loads and re-validates a recording. Imported entries come back marked as imported. */
  async load(id: string): Promise<{ meta: ReplayMeta; recording: RunRecording }> {
    const row = await this.row(id);
    try {
      const recording = await parseRecording(row.text);
      if (row.kind === 'imported') recording.meta.origin = 'imported';
      return { meta: meta(row), recording };
    } catch (err: unknown) {
      throw new StorageError('corrupt', `The recording "${row.name}" is damaged: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  /** The stored file text, for export. */
  async exportText(id: string): Promise<{ name: string; text: string }> {
    const row = await this.row(id);
    return { name: row.name, text: row.text };
  }

  /** The personal-best recording for a record key, if one is kept. */
  async pbFor(recordKey: string): Promise<ReplayMeta | null> {
    return (await this.list()).find((m) => m.kind === 'pb' && m.recordKey === recordKey) ?? null;
  }

  /**
   * Automatic PB save: replaces the previous PB recording of the same record
   * key (unless the player marked it as a favourite: then it is kept as a
   * saved recording).
   */
  async savePb(rec: RunRecording, trackKey: string, now = Date.now()): Promise<ReplayMeta> {
    const name = `PB ${formatSeconds(rec.result.seconds)}`;
    const text = await serializeRecording({ ...rec, meta: { ...rec.meta, name, origin: 'local' } });
    const all = await this.list();
    const prev = all.filter((m) => m.kind === 'pb' && m.recordKey === rec.track.recordKey);
    const kept = await Promise.all(prev.filter((m) => m.favourite).map((m) => this.row(m.id)));
    const row = this.makeRow('pb', rec, name, text, trackKey, now);
    checkSize(text);
    // The new PB is stored first: if that fails (full, quota), the previous PB recording is still there.
    await this.makeRoom(row.bytes, true, rec.track.recordKey);
    await this.put(row, 'Saving the PB recording failed');
    try {
      const tx = this.db.transaction('replays', 'readwrite');
      const store = tx.objectStore('replays');
      for (const m of prev) if (!m.favourite) store.delete(m.id);
      for (const r of kept) store.put({ ...r, kind: 'saved' satisfies ReplayKind });
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Removing the previous PB recording failed');
    }
    return meta(row);
  }

  /** Keeps a recording by hand. */
  async save(rec: RunRecording, name: string, trackKey: string, now = Date.now()): Promise<ReplayMeta> {
    const clean = cleanName(name) || `${rec.track.title} ${formatSeconds(rec.result.seconds)}`;
    const text = await serializeRecording({ ...rec, meta: { ...rec.meta, name: clean, origin: 'local' } });
    const row = this.makeRow('saved', rec, clean, text, trackKey, now);
    checkSize(text);
    await this.makeRoom(row.bytes, false, '');
    await this.put(row, 'Saving the recording failed');
    return meta(row);
  }

  /** Imports a recording file (validated as untrusted; stored view-only). */
  async importText(text: string, trackKey: string, now = Date.now()): Promise<ReplayMeta> {
    if (utf8Bytes(text) > MAX_RECORDING_BYTES) throw new StorageError('failed', `The file is larger than ${Math.round(MAX_RECORDING_BYTES / 1024)} KB.`);
    const rec = await parseRecording(text);
    const clean = cleanName(rec.meta.name) || `Imported ${formatSeconds(rec.result.seconds)}`;
    // Re-serialised from the validated recording: nothing of the original file is stored as-is.
    const stored = await serializeRecording({ ...rec, meta: { ...rec.meta, name: clean, origin: 'imported' } });
    const row = this.makeRow('imported', rec, clean, stored, trackKey, now);
    await this.makeRoom(row.bytes, false, '');
    await this.put(row, 'Importing the recording failed');
    return meta(row);
  }

  async rename(id: string, name: string): Promise<void> {
    const clean = cleanName(name);
    if (!clean) throw new StorageError('failed', 'A recording needs a name.');
    await this.update(id, (r) => ({ ...r, name: clean }));
  }

  async setFavourite(id: string, favourite: boolean): Promise<void> {
    await this.update(id, (r) => ({ ...r, favourite }));
  }

  async delete(id: string): Promise<void> {
    try {
      const tx = this.db.transaction('replays', 'readwrite');
      tx.objectStore('replays').delete(id);
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Deleting the recording failed');
    }
  }

  private makeRow(kind: ReplayKind, rec: RunRecording, name: string, text: string, trackKey: string, now: number): ReplayRow {
    return {
      id: newId(),
      kind,
      name,
      trackKey,
      trackTitle: cleanName(rec.track.title) || 'Unknown track',
      recordKey: rec.track.recordKey,
      createdAt: rec.meta.createdAt,
      savedAt: now,
      seconds: rec.result.seconds,
      outcome: rec.result.outcome,
      favourite: false,
      bytes: utf8Bytes(text),
      text,
    };
  }

  private async makeRoom(bytes: number, automatic: boolean, keepKey: string): Promise<void> {
    const all = await this.list();
    let count = all.length;
    let total = all.reduce((s, m) => s + m.bytes, 0);
    const fits = (): boolean => count + 1 <= MAX_ENTRIES && total + bytes <= MAX_TOTAL_BYTES;
    if (fits()) return;
    if (automatic) {
      // Oldest PB recordings of other track versions first; never favourites, saved or imported entries.
      const victims = all.filter((m) => m.kind === 'pb' && !m.favourite && m.recordKey !== keepKey).sort((a, b) => a.savedAt - b.savedAt);
      for (const v of victims) {
        if (fits()) break;
        await this.delete(v.id);
        count--;
        total -= v.bytes;
      }
      if (fits()) return;
    }
    throw new StorageError('quota', `The recording library is full (${MAX_ENTRIES} recordings or ${Math.round(MAX_TOTAL_BYTES / 1048576)} MB). Delete or export some recordings first.`);
  }

  private async row(id: string): Promise<ReplayRow> {
    let row: unknown;
    try {
      row = await req(this.db.transaction('replays').objectStore('replays').get(id));
    } catch (err: unknown) {
      throw wrap(err, 'Loading the recording failed');
    }
    if (!isRow(row)) throw new StorageError('missing', 'That recording no longer exists.');
    return row;
  }

  private async put(row: ReplayRow, what: string): Promise<void> {
    try {
      const tx = this.db.transaction('replays', 'readwrite');
      tx.objectStore('replays').put(row);
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, what);
    }
  }

  private async update(id: string, fn: (r: ReplayRow) => ReplayRow): Promise<void> {
    const row = await this.row(id);
    await this.put(fn(row), 'Updating the recording failed');
  }
}

/** A stored recording must load again: it may not exceed what parseRecording accepts. */
function checkSize(text: string): void {
  if (utf8Bytes(text) > MAX_RECORDING_BYTES) throw new StorageError('failed', `This recording is larger than ${Math.round(MAX_RECORDING_BYTES / 1048576)} MB, too long to keep.`);
}

export function formatSeconds(s: number | null): string {
  if (s === null) return '(unfinished)';
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return m > 0 ? `${m}:${rest.toFixed(3).padStart(6, '0')}` : rest.toFixed(3);
}
